import {
  type KeyboardEvent,
  useCallback,
  useLayoutEffect,
  useRef
} from 'react';
import {
  type FinishRowFacts,
  finishRowOutcome,
  rowListIntent,
  type RowListKeyTarget
} from 'v5/core/row-lists/rowListKeys';
import {
  findFocusableElement,
  getSequentialTabStopsWithin
} from 'v5/platform/dom/isFocusable';

/** `data-row-key` of the placeholder row’s input. */
export const PLACEHOLDER_ROW_KEY = 'placeholder';

/** The part a row’s first text input carries in `data-row-part`. */
export const VALUE_PART = 'value';

type RowFacts = Omit<FinishRowFacts, 'isLast' | 'insert'> & {
  /** Every text part of the row is empty. */
  blank: boolean;
};

/** Where Enter adds a row: the end (lists ordered by id), or right below (a list in the person’s own order). */
type RowListInsert =
  | { insert: 'end' }
  | {
      insert: 'below';
      /** Add an empty row right below; returns its key. */
      onInsertBelow(rowKey: string): string;
    };

/** A list in the person’s own order moves rows with ⌥↑ / ⌥↓; one ordered by id doesn’t. */
type RowListOrder =
  | { manualOrder: false }
  | { manualOrder: true; onMove(rowKey: string, direction: -1 | 1): void };

export type RowListKeyboardOptions = RowListInsert &
  RowListOrder & {
    /** Row keys in display order (not the placeholder). */
    rowKeys: ReadonlyArray<string>;
    rowFacts(rowKey: string): RowFacts;
    /** A hint stopped Enter once (lists whose rows can hint). */
    onAcknowledgeHint?: (rowKey: string) => void;
    /** A row Enter couldn't finish says why (lists whose rows can block Save). */
    onShowError?: (rowKey: string) => void;
    onRemove(rowKey: string): void;
    /**
     * The list is done (Enter on the empty placeholder, or on an empty last
     * row). By default focus goes to what follows the list in its form, or to
     * the form's submit button when nothing does.
     */
    onLeave?: () => void;
  };

/** Focuses a row's control, the caret after its text where it has one (not a radio or a checkbox). */
export function focusAtEnd(control: HTMLElement) {
  control.focus();
  if (control instanceof HTMLInputElement && control.selectionStart !== null) {
    const end = control.value.length;
    control.setSelectionRange(end, end);
  }
}

function focusAfter(list: HTMLElement) {
  const form = list.closest('form');
  if (!form) return;
  const next = getSequentialTabStopsWithin(form).find(
    element =>
      !list.contains(element) &&
      (list.compareDocumentPosition(element) &
        Node.DOCUMENT_POSITION_FOLLOWING) !==
        0
  );
  (next ?? form.querySelector<HTMLElement>('[type="submit"]'))?.focus();
}

function keyTarget(
  element: HTMLElement,
  isPlaceholder: boolean
): RowListKeyTarget {
  const text =
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
      ? element.value
      : null;
  if (isPlaceholder) return { type: 'placeholder', empty: (text ?? '') === '' };
  if (text === null) return { type: 'picker' };
  return { type: 'text', empty: text === '' };
}

/**
 * The keyboard of a list of rows: Enter finishes a row from any part, ⌘/Ctrl⌫
 * removes it, ⌫ in an empty row removes it, ↑ / ↓ move between rows, ⌥↑ / ⌥↓
 * reorder where the order is the person’s own. Rows mark their controls (or
 * an element around one) with `data-row-key` and `data-row-part`; a control
 * whose list is open (`aria-expanded="true"`) keeps its own keys. Spread
 * `listProps` on the list's root.
 */
export function useRowListKeyboard(options: RowListKeyboardOptions) {
  const latest = useRef(options);
  latest.current = options;
  const container = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<{ rowKey: string; part: string } | null>(null);

  const focusPart = useCallback((rowKey: string, part: string) => {
    const root = container.current;
    const target =
      root?.querySelector<HTMLElement>(
        `[data-row-key="${CSS.escape(rowKey)}"][data-row-part="${CSS.escape(part)}"]`
      ) ??
      root?.querySelector<HTMLElement>(
        `[data-row-key="${CSS.escape(rowKey)}"][data-row-part]`
      );
    const control = target && findFocusableElement(target);
    if (!control) return false;
    focusAtEnd(control);
    return true;
  }, []);

  /** Focus a row that may only render after the next commit (added, moved, the one above a removed row). */
  const focusRowSoon = useCallback(
    (rowKey: string, part: string = VALUE_PART) => {
      if (!focusPart(rowKey, part)) pendingFocus.current = { rowKey, part };
    },
    [focusPart]
  );

  useLayoutEffect(() => {
    const pending = pendingFocus.current;
    if (pending && focusPart(pending.rowKey, pending.part)) {
      pendingFocus.current = null;
    }
  });

  const onKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const element = event.target as HTMLElement;
      const marked = element.closest<HTMLElement>('[data-row-part]');
      if (!marked || !container.current?.contains(marked)) return;
      const { rowKey, rowPart: part } = marked.dataset;
      if (rowKey === undefined || part === undefined) return;
      if (element.getAttribute('aria-expanded') === 'true') return;
      // A combobox with text opens its list on ↓, as comboboxes do.
      if (
        event.key === 'ArrowDown' &&
        !event.altKey &&
        element.getAttribute('role') === 'combobox' &&
        element instanceof HTMLInputElement &&
        element.value.trim() !== ''
      ) {
        return;
      }

      const o = latest.current;
      const isPlaceholder = rowKey === PLACEHOLDER_ROW_KEY;
      const index = o.rowKeys.indexOf(rowKey);
      const target = keyTarget(element, isPlaceholder);
      // Enter in a multi-line text is a new line.
      if (element instanceof HTMLTextAreaElement && event.key === 'Enter') {
        return;
      }
      // An empty row that is a button adds a row when pressed.
      if (
        isPlaceholder &&
        element instanceof HTMLButtonElement &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        return;
      }
      const facts = isPlaceholder ? null : o.rowFacts(rowKey);
      const intent = rowListIntent({
        key: event.key,
        mod: event.metaKey || event.ctrlKey,
        alt: event.altKey,
        shift: event.shiftKey,
        // Safari ends the composition before the Enter that confirms it (keyCode 229).
        composing:
          event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229,
        repeat: event.repeat,
        modIsCtrl: event.ctrlKey && !event.metaKey,
        target,
        rowBlank: facts?.blank ?? true,
        isFirst: index === 0,
        isLast: index === o.rowKeys.length - 1,
        manualOrder: o.manualOrder
      });
      if (intent === null) return;
      event.preventDefault();
      event.stopPropagation();

      const neighbour = (offset: number) => o.rowKeys[index + offset];
      const leave = () => {
        if (o.onLeave) o.onLeave();
        else if (container.current) focusAfter(container.current);
      };
      switch (intent.type) {
        case 'list-done':
          leave();
          return;
        case 'focus-last-row': {
          const last = o.rowKeys[o.rowKeys.length - 1];
          if (last !== undefined) focusRowSoon(last);
          return;
        }
        case 'focus-row': {
          const next = neighbour(intent.direction);
          if (next !== undefined) focusRowSoon(next, part);
          else if (intent.direction === 1) focusRowSoon(PLACEHOLDER_ROW_KEY);
          return;
        }
        case 'move-row':
          if (!o.manualOrder) return;
          o.onMove(rowKey, intent.direction);
          focusRowSoon(rowKey, part);
          return;
        case 'remove-row': {
          const next = neighbour(-1) ?? neighbour(1) ?? PLACEHOLDER_ROW_KEY;
          o.onRemove(rowKey);
          focusRowSoon(next);
          return;
        }
        case 'finish-row': {
          if (facts === null) return;
          const outcome = finishRowOutcome({
            ...facts,
            isLast: index === o.rowKeys.length - 1,
            insert: o.insert
          });
          switch (outcome.type) {
            case 'drop-and-leave':
              o.onRemove(rowKey);
              leave();
              return;
            case 'show-error':
              o.onShowError?.(rowKey);
              focusRowSoon(rowKey, outcome.part);
              return;
            case 'stay-on-hint':
              o.onAcknowledgeHint?.(rowKey);
              return;
            case 'insert-below':
              if (o.insert === 'below') focusRowSoon(o.onInsertBelow(rowKey));
              return;
            case 'next-row': {
              const next = neighbour(1);
              if (next !== undefined) focusRowSoon(next);
              return;
            }
            case 'placeholder':
              focusRowSoon(PLACEHOLDER_ROW_KEY);
              return;
          }
        }
      }
    },
    [focusRowSoon]
  );

  return { listProps: { ref: container, onKeyDownCapture } };
}
